import React from "react";
import { useSearchParams } from "react-router-dom";
import classNames from "classnames";
import parsePhoneNumber from "libphonenumber-js";
import { trackEvent } from "../../services/analytics_v2";

import providerDirectorySearch from "../../components/ProviderSearch/providerDirectory/providerDirectorySearch";

import { Link } from "react-router-dom";
import { PhoneIcon } from "@heroicons/react/20/solid";

import { Listing } from "../../components/ProviderSearch/types";
import { Pagination } from "../../components/ProviderSearch/providerDirectory/nhsdDirectorySearch";

type QueryParams = {
  language: string;
  offset: number;
};

export default function ShowPlace() {
  const mapComponentRef = React.useRef<HTMLDivElement | null>(null);
  const [searchParams, _setSearchParams] = useSearchParams();
  const [map, setMap] = React.useState<google.maps.Map | null>(null);
  const [listingsLoading, setListingsLoading] = React.useState<boolean | null>(
    null,
  );
  const [listings, setListings] = React.useState<Listing[] | null>(null);
  const [pagination, setPagination] = React.useState<Pagination | null>(null);
  const [queryParams, setQueryParams] = React.useState<QueryParams>({
    language: "",
    offset: 0,
  });
  const [infoWindows, setInfoWindows] = React.useState<{
    [key: string]: google.maps.InfoWindow;
  } | null>(null);
  const [selectedListing, setSelectedListing] = React.useState<string | null>(
    null,
  );

  const rawLat = searchParams.get("lat");
  const rawLng = searchParams.get("lng");
  const name = searchParams.get("name");

  const buildListingUrl = (listing: Listing) => {
    return `/provider_search/${listing.id}?&return_lat=${rawLat}&return_lng=${rawLng}&return_name=${name}`;
  };

  const handleClickNextPage = () => {
    if (pagination === null) {
      return;
    }

    trackEvent("v2ps-show_location-change_page");

    setQueryParams({
      ...queryParams,
      offset: queryParams.offset + pagination.limit,
    });
  };

  const handleClickPreviousPage = () => {
    if (pagination === null) {
      return;
    }

    trackEvent("v2ps-show_location-change_page");

    setQueryParams({
      ...queryParams,
      offset: queryParams.offset - pagination.limit,
    });
  };

  const handleChangeLanguage = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const language = event.target.value;

    trackEvent("v2ps-show_location-change_language", {
      language,
    });

    setQueryParams({
      ...queryParams,
      language,
      offset: 0,
    });
  };

  const handleClickPhone = (listing: Listing) => {
    trackEvent("v2ps-show_location-clicked_phone", {
      name: listing.displayName,
    });
  };

  // Create a Google map on the lat/lng coordinates
  React.useEffect(() => {
    if (!rawLat || !rawLng || !mapComponentRef.current) {
      return;
    }

    const lat = parseFloat(rawLat);
    const lng = parseFloat(rawLng);

    if (map === null) {
      const returnedMap = new window.google.maps.Map(mapComponentRef.current, {
        center: { lat, lng },
        zoom: 16,
        disableDefaultUI: true,
        mapId: "1741826b4ec7ee78",
      });

      setMap(returnedMap);
    } else {
      console.warn("Handle map update");
    }
  }, [rawLat, rawLng]);

  // Load search results
  React.useEffect(() => {
    (async () => {
      if (!rawLat || !rawLng) {
        return;
      }

      setListingsLoading(true);

      const [parsedResults, pagination] = await providerDirectorySearch({
        params: {
          ...(queryParams === null
            ? {}
            : {
                offset: queryParams.offset.toString(),
                language: queryParams.language,
              }),
          latitude: rawLat,
          longitude: rawLng,
        },
      });

      setPagination(pagination);
      setListingsLoading(false);
      setListings(parsedResults);
    })();
  }, [rawLat, rawLng, queryParams]);

  // Create info windows from each of the listings, these will be used with
  // the markers and shown when clicked.
  React.useEffect(() => {
    (async () => {
      if (listings === null || listings.length === 0 || map === null) {
        return;
      }

      // Build info windows then map them to the id of the listing for lookup
      // when a marker is clicked
      const createdInfowindows = listings
        .reduce((acc: google.maps.InfoWindow[], listing) => {
          if (!listing.address) {
            return acc;
          }

          let address = [];

          if (listing.address.addressLine1) {
            address.push(listing.address.addressLine1);
          }

          if (listing.address.addressLine2) {
            address.push(listing.address.addressLine2);
          }

          if (listing.address.addressLine3) {
            address.push(listing.address.addressLine3);
          }

          if (listing.address.suburb) {
            address.push(listing.address.suburb);
          }

          const infowindow = new google.maps.InfoWindow({
            content: `
              <div>
                <h2 class="text-base mt-0 font-bold">${listing.displayName}</h2>
                <address class="not-italic my-2">
                  ${address.join("<br />")}
                </address>
                <a href=${buildListingUrl(
                  listing,
                )} class="text-blue-700 underline">Show details</a>
              </div>
              `,
            ariaLabel: "Uluru",
          });

          acc.push(infowindow);

          return acc;
        }, [])
        .reduce(
          (
            acc: { [key: string]: google.maps.InfoWindow },
            createdInfowindow,
            idx,
          ) => {
            return {
              ...acc,
              [listings[idx].id]: createdInfowindow,
            };
          },
          {},
        );

      setInfoWindows(createdInfowindows);
    })();
  }, [listings]);

  // When a listing is selected, scroll it into view
  React.useEffect(() => {
    if (selectedListing === null) {
      return;
    }

    const el = document.getElementById(`listing-${selectedListing}`);

    // Only scroll into view when in desktop mode
    if (el && screen.width >= 768) {
      el.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  }, [selectedListing]);

  // Create markers for each of the listings
  React.useEffect(() => {
    let createdMarkers: Array<google.maps.Marker> = [];

    (async () => {
      if (listings === null || listings.length === 0 || map === null) {
        return;
      }

      // Create each of the markers based on the listings returned from the
      // API.
      createdMarkers = listings.reduce((acc: google.maps.Marker[], listing) => {
        if (!listing.address || !listing.address.lat || !listing.address.lng) {
          return acc;
        }

        const marker = new google.maps.Marker({
          position: {
            lat: parseFloat(listing.address.lat),
            lng: parseFloat(listing.address.lng),
          },
          map,
          title: listing.displayName,
        });

        marker.addListener("click", () => {
          setSelectedListing(null);

          if (infoWindows === null) {
            return;
          }

          Object.values(infoWindows).forEach((infoWindow) => {
            infoWindow.close();
          });

          if (infoWindows[listing.id]) {
            trackEvent("v2ps-show_location-clicked_marker", {
              name: listing.displayName,
            });

            setSelectedListing(listing.id);

            infoWindows[listing.id].open({
              anchor: marker,
              map,
            });
          }
        });

        acc.push(marker);

        return acc;
      }, []);

      const bounds = new google.maps.LatLngBounds();

      createdMarkers.forEach((createdMarker) => {
        // For each of the markers, extend the bounds object that will be given
        // to the map, this allows us to fit all the markers into the viewport
        const position = createdMarker.getPosition();
        if (position) {
          bounds.extend(position);
        }
      });

      map.fitBounds(bounds);
    })();

    return () => {
      createdMarkers.forEach((createdMarker) => {
        createdMarker.setMap(null);
      });
    };
  }, [listings, infoWindows]);

  const renderedLoading = (() => {
    if (listingsLoading === null || listingsLoading === true) {
      return <p className="my-4 font-italic">Loading listings...</p>;
    }
  })();

  const renderedListingTiles = (() => {
    if (listings === null) {
      return null;
    }

    return listings.map((listing) => {
      const classes = classNames({
        "p-2 bg-white shadow-md h-full flex flex-col border-2": true,
        "border-black": listing.id === selectedListing,
        "border-white": listing.id !== selectedListing,
      });

      const parsedPhoneNumber =
        listing && listing.phoneNumber
          ? parsePhoneNumber(listing.phoneNumber, "AU")
          : null;

      return (
        <div id={`listing-${listing.id}`} key={listing.id} className={classes}>
          <h2 className="text-lg mt-0 mb-4">{listing.displayName}</h2>
          <div className="flex flex-col flex-grow">
            {listing.address && (
              <address className="m-0 not-italic">
                {listing.address.addressLine1 && (
                  <div>{listing.address.addressLine1}</div>
                )}
                {listing.address.addressLine2 && (
                  <div>{listing.address.addressLine2}</div>
                )}
                {listing.address.addressLine3 && (
                  <div>{listing.address.addressLine3}</div>
                )}
                {listing.address.suburb && <div>{listing.address.suburb}</div>}
              </address>
            )}
            {parsedPhoneNumber && (
              <a
                className="my-4 flex items-center gap-2 block"
                href={`tel:${parsedPhoneNumber.formatNational()}`}
                onClick={() => handleClickPhone(listing)}
              >
                <PhoneIcon width={"20px"} />{" "}
                {parsedPhoneNumber.formatNational()}
              </a>
            )}
            <div className="flex-grow"></div>
            {listing.bulkBills === true && (
              <div>
                <div className="text-xs inline-flex items-center font-bold leading-sm uppercase px-3 py-1 bg-green-200 text-green-700 rounded-full">
                  Bulk bills
                </div>
              </div>
            )}
          </div>
          <Link
            to={buildListingUrl(listing)}
            className="text-center block mt-2 border-t border-1 underline text-blue-700 pt-2"
            aria-label={`Show details for ${listing.displayName}`}
          >
            Show details
          </Link>
        </div>
      );
    });
  })();

  const renderedPagination = (() => {
    if (pagination === null) {
      return null;
    }

    return (
      <div className="flex items-center">
        <div className="basis-1/3 text-left">
          {pagination.offset > 0 && (
            <a
              className="underline cursor-pointer"
              onClick={handleClickPreviousPage}
            >
              Previous page
            </a>
          )}
        </div>
        <div className="basis-1/3 text-center"></div>
        <div className="basis-1/3 text-right">
          {pagination.offset + pagination.limit <= pagination.count && (
            <a
              className="underline cursor-pointer"
              onClick={handleClickNextPage}
            >
              Next page
            </a>
          )}
        </div>
      </div>
    );
  })();

  return (
    <div className="p-4 md:p-8 flex flex-col md:flex-row items-stretch gap-4 md:gap-8 h-full md:h-[1000px]">
      <div className="basis-1/2 md:order-last">
        <div
          ref={mapComponentRef}
          className="w-full h-full min-h-[250px] md:min-h-[500px]"
        />
      </div>
      <div className="basis-1/2 flex flex-col">
        <div className="flex items-center gap-2">
          <Link className="underline" to={`/provider_search`}>
            Back to search
          </Link>
          <span>&gt;</span>
          <span>{name}</span>
        </div>
        <h1 className="text-4xl my-4 md:my-8">{name}</h1>
        <div className="flex items-center justify-center my-4 gap-4 border border-gray-200 rounded px-4 py-2 bg-white">
          <label htmlFor="languageSelect">Preferred language spoken</label>
          <select
            id="languageSelect"
            className="w-full md:w-auto"
            onChange={handleChangeLanguage}
          >
            <option value="en">English</option>
            <option value="ar">Arabic</option>
            <option value="zh">Chinese</option>
            <option value="vi">Vietnamese</option>
            <option value="it">Italian</option>
            <option value="el">Greek</option>
            <option value="tl">Tagalog</option>
            <option value="hi">Hindi</option>
            <option value="es">Spanish</option>
            <option value="pa">Punjabi</option>
          </select>
        </div>
        <div className="flex-grow overflow-y-scroll">
          {renderedLoading}
          {renderedListingTiles &&
            renderedListingTiles.length > 0 &&
            listingsLoading === false && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {renderedListingTiles}
              </div>
            )}
          {renderedListingTiles &&
            renderedListingTiles.length === 0 &&
            listingsLoading === false && (
              <div className="flex items-center justify-center text-italic text-xl italic p-4">
                No results found
              </div>
            )}
        </div>
        <div className="mt-4 text-center">{renderedPagination}</div>
      </div>
    </div>
  );
}
